// ---------------------------------------------------------------------------
// S3 as-is capture — the pasted legacy screen, reconstructed as a renderable
// cogo block so the conversion has an ORIGINAL to show, not just a description
// of one (operator directive 2026-08-12: capture at paste time).
//
// `parseLegacyScreenReadback` already reads the screen's composition
// deterministically (no model in the loop). This module turns that reading
// into the `settings.asIsSnapshot` the As-Is mode renders through the ordinary
// preview pipeline.
//
// HONESTY BOUNDARY. Only what the readback could DECIDE is rendered: fields,
// table columns, and action labels. Server logic does not travel with a screen
// (the readback's own contract — scriptlets are counted, never carried), so
// nothing here implies behaviour that was not read. The blocks are marked
// `sourceDerived`, which makes the preview pipeline render them verbatim
// instead of tokenizing them through the design compiler — an "original" that
// had been restyled would not be the original.
// ---------------------------------------------------------------------------

import type { AsIsSnapshot } from '@/features/preview/services/as-is-snapshot'
import { AS_IS_SNAPSHOT_SCHEMA_VERSION } from '@/features/preview/services/as-is-snapshot'
import {
  findCommentEnd,
  sanitizeAsIsMarkup,
} from '@/features/preview/services/as-is-markup'
import type { LegacyScreenReadback } from './legacy-screen-readback'

const AS_IS_BLOCK_NAME = 'as-is-screen'

interface CogoNode {
  id: string
  type: string
  text?: string
  className?: string
  options?: Record<string, unknown>
  columns?: Array<{ key: string; label: string }>
  children?: CogoNode[]
}

function fieldNode(field: { name: string; control: string }, index: number): CogoNode {
  return {
    id: `as-is-field-${index}`,
    type: 'container',
    className: 'flex flex-col gap-1',
    children: [
      {
        id: `as-is-field-${index}-label`,
        type: 'text',
        text: field.name,
        className: 'text-sm text-muted-foreground',
      },
      {
        // The control tag is what the screen actually used; it is reported as
        // read rather than mapped to a richer cogo control.
        id: `as-is-field-${index}-control`,
        type: field.control === 'hidden' ? 'text' : 'input',
        ...(field.control === 'hidden'
          ? { text: '(hidden)', className: 'text-xs text-muted-foreground' }
          : {}),
      },
    ],
  }
}

function tableNode(table: { columns: string[] }, index: number): CogoNode {
  return {
    id: `as-is-table-${index}`,
    type: 'data-table',
    className: 'w-full',
    columns: table.columns.map((label, columnIndex) => ({
      key: `col-${columnIndex}`,
      label,
    })),
  }
}

function actionNode(action: { label: string }, index: number): CogoNode {
  return {
    id: `as-is-action-${index}`,
    type: 'button',
    text: action.label,
  }
}

/**
 * fix-round-2 (Task 3) — trims a pasted segment down to its markup region.
 * A raw segment can carry human prose alongside the screen: S7's "이 화면처럼
 * 만들어줘" intent line above the pasted screen is the concrete case the
 * reviewer found (`ReferenceExtractionReadback.intent`, see
 * reference-extraction-readback.ts), but the principle is general — the
 * As-Is markup source is the markup, not the prose around it. Strips
 * everything BEFORE the first `<` and AFTER the last `>`; deliberately does
 * NOT touch anything between them, even non-tag text — content interleaved
 * inside the pasted document is part of the document, not prose to strip.
 * A no-op for markup that already starts/ends with a tag (the ordinary S3
 * paste, and every override-free caller whose `prompt` already IS pure
 * markup).
 */
function trimToMarkupRegion(text: string): string {
  let first = -1
  let end = -1
  for (const token of markupTokens(text)) {
    if (first === -1) first = token.start
    end = token.end
  }
  // No markup token at all: nothing to anchor on, so nothing is trimmed. The
  // sanitizer downstream is what decides such a paste carries no markup.
  if (first === -1 || end === -1) return text
  return text.slice(first, end)
}

/**
 * One markup token: an element tag, a comment, a doctype, a JSP/ASP scriptlet
 * or an XML processing instruction. Used only to ANCHOR the trim — never to
 * parse — so it is deliberately permissive inside a tag and strict about what
 * may follow the `<`.
 *
 * The `<` and `>` characters occur in ordinary prose around a paste: "금액 <
 * 100 인 경우" above it, "참고 => 이대로" below it. Anchoring on the first `<`
 * and the last `>` of the TEXT made both of those the boundary of the markup,
 * so the prose rode into the frame labelled 원본. Anchoring on the first and
 * last TOKEN cannot: neither `< 1` nor `=>` is one. `<%` is included on
 * purpose — a JSP paste opens with a scriptlet block, and an HTML-only anchor
 * would silently drop it.
 *
 * The tag body steps over QUOTED attribute values, because `>` is legal inside
 * one. A body of `[^>]*` ended the token at the first `>` it met, so a paste
 * ending on `<span title="a>b">` was trimmed to `…title="a>` — the anchor fix
 * cutting a tag in half. It only shows when such a tag is the LAST token,
 * which is why it survived the first round.
 */
const MARKUP_TOKEN_PATTERN =
  /<(?:%[\s\S]*?%|\?[\s\S]*?\?|[!/]?[a-zA-Z](?:"[^"]*"|'[^']*'|[^>'"])*)>/y

/**
 * The tokens, in order. Comments are NOT in the pattern above: a comment
 * closes four ways (`-->`, `--!>`, and the abrupt `<!-->` / `<!--->`), and
 * that question already has an answer — `findCommentEnd` in as-is-markup.ts.
 *
 * FINAL GATE (Codex P2): the pattern's old comment alternative recognised only
 * `-->`, so `<!-->VISIBLE<div>…</div>` found its first token at `<div>` and
 * the trim deleted `VISIBLE` — visible source text, gone before the sanitizer
 * ever saw it. An unterminated comment yields no token at all, exactly like an
 * unterminated tag: there is nothing complete to anchor on.
 */
function* markupTokens(text: string): Generator<{ start: number; end: number }> {
  let i = 0
  while (i < text.length) {
    const lt = text.indexOf('<', i)
    if (lt === -1) return
    if (text.startsWith('<!--', lt)) {
      const commentEnd = findCommentEnd(text, lt)
      if (commentEnd === -1) return
      yield { start: lt, end: commentEnd }
      i = commentEnd
      continue
    }
    MARKUP_TOKEN_PATTERN.lastIndex = lt
    const match = MARKUP_TOKEN_PATTERN.exec(text)
    if (match) {
      yield { start: lt, end: lt + match[0].length }
      i = lt + match[0].length
    } else {
      i = lt + 1
    }
  }
}

/**
 * Build the as-is snapshot from a legacy-screen readback.
 *
 * `capturedAt` is injected rather than read from the clock so the result is
 * deterministic and testable.
 *
 * Returns null when the readback carries nothing renderable — a screen we
 * could not read must not become an empty "original" that looks like the
 * conversion lost the source.
 *
 * `rawMarkup`, when given, is the pasted original verbatim. It is sanitized
 * and attached as `sourceMarkup` so the As-Is tab can render the real markup
 * instead of the derived skeleton. A failed sanitize (empty-after-clean or
 * over-cap) is not fatal to the snapshot — the skeleton still returns; only
 * `sourceMarkup` is omitted.
 */
export function buildAsIsSnapshotFromLegacyScreen(
  readback: LegacyScreenReadback,
  capturedAt: string,
  rawMarkup?: string,
): AsIsSnapshot | null {
  const children: CogoNode[] = []

  const fieldNodes = readback.fields.map(fieldNode)
  if (fieldNodes.length > 0) {
    children.push({
      id: 'as-is-fields',
      type: 'container',
      className: 'grid grid-cols-1 sm:grid-cols-2 gap-4',
      children: fieldNodes,
    })
  }

  // Header-less tables are layout scaffolding in legacy markup, not data — the
  // readback records them, but rendering an empty grid would invent content.
  for (const [index, table] of readback.tables.entries()) {
    if (table.columns.length === 0) continue
    children.push(tableNode(table, index))
  }

  const actionNodes = readback.actions.map(actionNode)
  if (actionNodes.length > 0) {
    children.push({
      id: 'as-is-actions',
      type: 'container',
      className: 'flex flex-row gap-2',
      children: actionNodes,
    })
  }

  if (children.length === 0) return null

  const root: CogoNode = {
    id: 'as-is-root',
    type: 'container',
    className: 'flex flex-col gap-6 p-6',
    // The marker the preview pipeline keys on to render source truth verbatim.
    options: { sourceDerived: true },
    children,
  }

  // 원문이 있으면 그것이 소스 진실이다. 정제 실패·상한 초과는 조용히 넘어가되
  // 스냅샷 자체는 살린다 — 골격이라도 있는 편이 아무것도 없는 것보다 낫고,
  // 무엇이 없는지는 UI가 라벨로 말한다. 트림은 여기 한 곳에서만 -- 어느
  // 호출자도(override든 폴백 prompt든) 잊을 수 없도록.
  const sourceMarkup = rawMarkup
    ? sanitizeAsIsMarkup(trimToMarkupRegion(rawMarkup)) ?? undefined
    : undefined

  return {
    schemaVersion: AS_IS_SNAPSHOT_SCHEMA_VERSION,
    sourceKind: 'legacy-screen',
    capturedAt,
    blocks: { [AS_IS_BLOCK_NAME]: { ui: { root } } },
    ...(sourceMarkup ? { sourceMarkup } : {}),
  }
}
